import { RefreshCw, ShieldQuestion, UserX } from 'lucide-react';
import { Link } from 'react-router-dom';

export type EpcProjectAccessStateProps =
  | { variant: 'no-access'; onRetry?: never }
  | { variant: 'error'; onRetry: () => void };

/**
 * Shown on /epc/projects/:id when the caller's project capability cannot map to
 * a workspace: either they hold no active membership (no-access) or the
 * memberships fetch failed so their capability is unknown (error). Replaces the
 * old behaviour of falling through to the governance (PMO) workspace.
 */
export default function EpcProjectAccessState(props: EpcProjectAccessStateProps) {
  const isError = props.variant === 'error';
  const Icon = isError ? ShieldQuestion : UserX;

  return (
    <div className="flex min-h-[320px] items-center justify-center p-6">
      <div
        className="w-full max-w-md rounded-2xl px-8 py-10 text-center"
        style={{
          background: '#FFFEFB',
          border: '1px solid rgba(11,59,46,0.16)',
          boxShadow: '0 1px 2px rgba(11,59,46,0.04), 0 18px 40px -24px rgba(11,59,46,0.20)',
        }}
      >
        <span
          className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
          style={{ background: '#F8F2E2', border: '1px solid rgba(11,59,46,0.14)' }}
        >
          <Icon className="h-6 w-6" style={{ color: '#1F5A47' }} strokeWidth={2} aria-hidden />
        </span>
        <h2 className="text-base font-bold" style={{ color: '#0B3B2E' }}>
          {isError ? "Couldn't verify your access" : "You don't have a role on this project"}
        </h2>
        <p className="mt-2 text-sm" style={{ color: '#7C8A82' }}>
          {isError
            ? 'Your project role could not be loaded, so the workspace cannot be shown yet.'
            : 'Ask the project PMO or head to add you to the project team.'}
        </p>
        {isError ? (
          <button
            type="button"
            onClick={props.onRetry}
            className="mt-6 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #1F5A47, #0B3B2E)' }}
          >
            <RefreshCw className="h-4 w-4" strokeWidth={2.2} aria-hidden />
            Retry
          </button>
        ) : (
          <Link
            to="/epc/projects"
            className="mt-6 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition hover:bg-[rgba(31,90,71,0.08)]"
            style={{ color: '#1F5A47', border: '1px solid rgba(11,59,46,0.2)', background: '#fcfaf3' }}
          >
            Back to EPC projects
          </Link>
        )}
      </div>
    </div>
  );
}
